import { motion, useScroll, useTransform } from "framer-motion";
import { useI18n } from "@/i18n/I18nContext";
import { useRef } from "react";
import ContactForm from "@/components/ContactForm";
import Marquee from "@/components/Marquee";
import { ArrowRight, ArrowUpRight, MapPin } from "lucide-react";

/* -------- animations helpers -------- */
const fadeUp = {
  initial: { y: 40, opacity: 0 },
  whileInView: { y: 0, opacity: 1 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
};

const LineReveal = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.05, ease: [0.16, 1, 0.3, 1], delay }}
      className="block"
    >
      {children}
    </motion.span>
  </span>
);

/* -------- Home -------- */
export default function Home() {
  const { t, lang } = useI18n();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <main className="bg-[#F8F7F4] text-[#121316]" style={{ wordBreak: "break-word" }}>
      {/* HERO */}
      <section ref={heroRef} className="relative pt-32 sm:pt-40 pb-16 lg:pb-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="text-[11px] font-mono uppercase tracking-[0.24em] text-[#7A7B82]"
            >
              {t("hero.eyebrow")}
            </motion.p>
            <h1 className="mt-6 font-serif text-[13vw] sm:text-7xl lg:text-[92px] leading-[0.95] tracking-tight text-[#121316]">
              <LineReveal delay={0.1}>{t("hero.titleLine1")}</LineReveal>
              <LineReveal delay={0.28}>
                <span className="italic text-[#C27835]">{t("hero.titleLine2")}</span>
              </LineReveal>
              <LineReveal delay={0.46}>{t("hero.titleLine3")}</LineReveal>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.75 }}
              className="mt-8 max-w-xl text-lg sm:text-xl text-[#3B3C42] leading-relaxed font-light"
            >
              {t("hero.subtitle")}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.9 }}
              className="mt-10 flex flex-wrap gap-3"
            >
              <a
                href={`/${lang}#projects`}
                data-testid="hero-cta-explore"
                className="inline-flex items-center gap-2 bg-[#121316] text-[#F8F7F4] rounded-full px-6 py-3.5 text-sm font-medium hover:bg-[#C27835] transition-colors duration-300"
              >
                {t("hero.ctaPrimary")}
                <ArrowRight size={16} />
              </a>
              <a
                href={`/${lang}#contact`}
                data-testid="hero-cta-contact"
                className="inline-flex items-center gap-2 border border-[#121316]/30 rounded-full px-6 py-3.5 text-sm font-medium text-[#121316] hover:border-[#121316] hover:bg-[#121316] hover:text-[#F8F7F4] transition-colors duration-300"
              >
                {t("hero.ctaSecondary")}
                <ArrowUpRight size={16} />
              </a>
            </motion.div>
          </div>

          <div className="lg:col-span-4">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.65 }}
              className="relative aspect-[3/4] overflow-hidden rounded-sm bg-[#2B3D34]"
            >
              <motion.img
                src="https://images.unsplash.com/photo-1611865422861-391cf5f3ba49?auto=format&fit=crop&w=1600&q=85"
                alt="High-rise en Puerto Madero"
                style={{ y, scale }}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#121316]/60 pointer-events-none" />
              <div className="absolute bottom-4 left-4 right-4 text-[#F8F7F4] flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em]">
                <MapPin size={14} /> Puerto Madero · CABA · 34°36′S 58°21′W
              </div>
            </motion.div>
          </div>
        </div>

        {/* Stats strip */}
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 mt-20 lg:mt-28">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 border-t border-[#E5E3DC] pt-10">
            {[
              { label: t("hero.stat1Label"), value: t("hero.stat1Value") },
              { label: t("hero.stat2Label"), value: t("hero.stat2Value") },
              { label: t("hero.stat3Label"), value: t("hero.stat3Value") },
            ].map((s, i) => (
              <motion.div key={i} {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 * i }}>
                <p className="text-[10.5px] font-mono uppercase tracking-[0.24em] text-[#7A7B82]">{s.label}</p>
                <p className="mt-3 font-serif text-4xl lg:text-5xl leading-none">{s.value}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <Marquee text={t("marquee.text")} />

      {/* WHAT */}
      <section id="what" className="py-24 lg:py-36">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <motion.p {...fadeUp} className="text-[11px] font-mono uppercase tracking-[0.24em] text-[#7A7B82]">
              {t("what.eyebrow")}
            </motion.p>
            <motion.h2 {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.08 }} className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              {t("what.title")}
            </motion.h2>
            <div className="mt-8 grid gap-6">
              {[1, 2, 3].map((n) => (
                <motion.div
                  key={n}
                  {...fadeUp}
                  transition={{ ...fadeUp.transition, delay: 0.05 * n }}
                  className="border-t border-[#E5E3DC] pt-5"
                >
                  <p className="font-mono text-[11px] tracking-[0.22em] text-[#C27835]">0{n}</p>
                  <h3 className="mt-2 font-serif text-2xl leading-snug">{t(`what.pillar${n}Title`)}</h3>
                  <p className="mt-2 text-[15px] text-[#52535A] leading-relaxed">{t(`what.pillar${n}Body`)}</p>
                </motion.div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-7 space-y-6 text-[17px] leading-[1.7] text-[#3B3C42] font-light">
            <motion.p {...fadeUp}>{t("what.p1")}</motion.p>
            <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.05 }}>{t("what.p2")}</motion.p>
            <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }}>{t("what.p3")}</motion.p>
            <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.15 }}>{t("what.p4")}</motion.p>

            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.2 }} className="relative mt-10 overflow-hidden rounded-sm aspect-[16/9]">
              <img
                src="https://images.unsplash.com/photo-1619218070141-bcfeb8b93074?auto=format&fit=crop&w=1600&q=85"
                alt="Vista aérea de Palermo, Buenos Aires"
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* HOW */}
      <section id="how" className="bg-[#121316] text-[#F8F7F4] py-24 lg:py-36 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <motion.p {...fadeUp} className="text-[11px] font-mono uppercase tracking-[0.24em] text-[#C27835]">
            {t("how.eyebrow")}
          </motion.p>
          <motion.h2 {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.08 }} className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl">
            {t("how.title")}
          </motion.h2>
          <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.14 }} className="mt-6 max-w-2xl text-[17px] text-[#B4B5BB] leading-relaxed font-light">
            {t("how.intro")}
          </motion.p>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((n) => (
              <motion.div
                key={n}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: 0.06 * n }}
                className="border-t border-[#2A2B30] pt-6 group"
              >
                <p className="font-mono text-xs text-[#C27835] tracking-[0.22em]">{t(`how.step${n}Num`)}</p>
                <h3 className="mt-3 font-serif text-2xl leading-snug group-hover:text-[#C27835] transition-colors duration-300">{t(`how.step${n}Title`)}</h3>
                <p className="mt-3 text-sm text-[#B4B5BB] leading-relaxed">{t(`how.step${n}Body`)}</p>
              </motion.div>
            ))}
          </div>

          <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.3 }} className="mt-16 max-w-2xl text-[17px] text-[#B4B5BB] leading-relaxed font-light">
            {t("how.closing")}
          </motion.p>
        </div>
      </section>

      {/* PROJECTS */}
      <section id="projects" className="py-24 lg:py-36">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-14">
            <div className="lg:col-span-7">
              <motion.p {...fadeUp} className="text-[11px] font-mono uppercase tracking-[0.24em] text-[#7A7B82]">
                {t("projects.eyebrow")}
              </motion.p>
              <motion.h2 {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.08 }} className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                {t("projects.title")}
              </motion.h2>
            </div>
            <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.14 }} className="lg:col-span-5 text-[16px] text-[#52535A] leading-relaxed font-light">
              {t("projects.intro")}
            </motion.p>
          </div>

          {/* Bento grid */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 lg:gap-6">
            <ProjectCard
              testId="project-card-madero"
              className="md:col-span-7 aspect-[4/3] md:aspect-auto md:h-[560px]"
              image="https://images.unsplash.com/photo-1611865422861-391cf5f3ba49?auto=format&fit=crop&w=1600&q=85"
              location={t("projects.p1Location")}
              title={t("projects.p1Title")}
              meta={t("projects.p1Meta")}
              cta={t("projects.cta")}
            />
            <ProjectCard
              testId="project-card-palermo"
              className="md:col-span-5 aspect-[4/3] md:aspect-auto md:h-[560px]"
              image="https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=1200&q=85"
              location={t("projects.p2Location")}
              title={t("projects.p2Title")}
              meta={t("projects.p2Meta")}
              cta={t("projects.cta")}
              tone="dark"
            />
            <ProjectCard
              testId="project-card-retiro"
              className="md:col-span-5 aspect-[4/3] md:aspect-auto md:h-[520px]"
              image="https://images.unsplash.com/photo-1615493932275-441bb55ba0bf?auto=format&fit=crop&w=1200&q=85"
              location={t("projects.p3Location")}
              title={t("projects.p3Title")}
              meta={t("projects.p3Meta")}
              cta={t("projects.cta")}
              tone="dark"
            />
            <ProjectCard
              testId="project-card-nunez"
              className="md:col-span-7 aspect-[4/3] md:aspect-auto md:h-[520px]"
              image="https://images.unsplash.com/photo-1611869635197-e82b4e57bac3?auto=format&fit=crop&w=1600&q=85"
              location={t("projects.p4Location")}
              title={t("projects.p4Title")}
              meta={t("projects.p4Meta")}
              cta={t("projects.cta")}
            />
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section id="trust" className="py-24 lg:py-36 bg-[#F1EEE7]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <motion.p {...fadeUp} className="text-[11px] font-mono uppercase tracking-[0.24em] text-[#7A7B82]">
              {t("trust.eyebrow")}
            </motion.p>
            <motion.h2 {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.08 }} className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              {t("trust.title")}
            </motion.h2>
            <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.14 }} className="mt-6 text-[16px] text-[#52535A] leading-relaxed font-light">
              {t("trust.body")}
            </motion.p>
            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.2 }} className="mt-8 inline-flex items-center gap-3 border border-[#121316]/25 rounded-full px-4 py-2">
              <span className="w-2 h-2 rounded-full bg-[#C27835] animate-pulse" />
              <span className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#121316]">{t("trust.riskBadge")}</span>
            </motion.div>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.05 }} className="bg-[#121316] text-[#F8F7F4] p-8 rounded-sm min-h-[280px] flex flex-col justify-between">
              <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#C27835]">{t("trust.impactTitle")}</p>
              <p className="font-serif text-2xl leading-snug mt-6">{t("trust.impactBody")}</p>
            </motion.div>
            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="bg-white p-8 rounded-sm min-h-[280px] flex flex-col justify-between border border-[#E5E3DC]">
              <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#7A7B82]">{t("trust.missionTitle")}</p>
              <p className="font-serif text-2xl leading-snug mt-6 text-[#121316]">{t("trust.missionBody")}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-24 lg:py-36">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <motion.p {...fadeUp} className="text-[11px] font-mono uppercase tracking-[0.24em] text-[#7A7B82]">
              {t("contact.eyebrow")}
            </motion.p>
            <motion.h2 {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.08 }} className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              {t("contact.title")}
            </motion.h2>
            <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.14 }} className="mt-6 text-[16px] text-[#52535A] leading-relaxed font-light">
              {t("contact.intro")}
            </motion.p>
            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.2 }} className="mt-10 space-y-2 text-sm text-[#52535A]">
              <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#7A7B82]">CABA · Argentina</p>
              <p>Av. del Libertador 1480, Piso 12</p>
              <p>C1112ABB · Ciudad Autónoma de Buenos Aires</p>
              <p><a href="mailto:hola@zantravela.com" className="underline underline-offset-4 hover:text-[#C27835]" data-testid="contact-email-link">hola@zantravela.com</a></p>
            </motion.div>
          </div>
          <div className="lg:col-span-7 bg-white border border-[#E5E3DC] rounded-sm p-6 sm:p-10">
            <ContactForm />
          </div>
        </div>
      </section>
    </main>
  );
}

function ProjectCard({ image, location, title, meta, cta, className = "", tone = "light", testId }) {
  const isDark = tone === "dark";
  return (
    <motion.article
      whileHover={{ y: -4 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className={`relative overflow-hidden rounded-sm bg-[#0f1013] group ${className}`}
      data-testid={testId}
    >
      <img src={image} alt={title} className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:scale-[1.03] transition-transform duration-[900ms] ease-out will-change-transform" />
      <div className={`absolute inset-0 ${isDark ? "bg-gradient-to-t from-[#121316]/85 via-[#121316]/25 to-transparent" : "bg-gradient-to-t from-[#121316]/70 via-transparent to-transparent"}`} />
      <div className="relative h-full p-6 sm:p-8 flex flex-col justify-between text-[#F8F7F4]">
        <div className="flex items-center gap-2 text-[10.5px] font-mono uppercase tracking-[0.22em]">
          <span className="w-1.5 h-1.5 rounded-full bg-[#C27835]" /> {location}
        </div>
        <div>
          <h3 className="font-serif text-2xl sm:text-3xl lg:text-4xl leading-[1.05] max-w-[22ch]">{title}</h3>
          <p className="mt-3 font-mono text-[11.5px] tracking-[0.18em] uppercase text-[#D8D9DE]">{meta}</p>
          <a
            href="#contact"
            data-testid={`${testId}-cta`}
            className="mt-6 inline-flex items-center gap-2 text-sm font-medium border-b border-[#C27835] pb-0.5 text-[#F8F7F4] hover:text-[#C27835] transition-colors duration-200"
          >
            {cta} <ArrowUpRight size={14} />
          </a>
        </div>
      </div>
    </motion.article>
  );
}
